import { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { skillCreateFlowSession } from './skill-create-flow-session';
import {
	CREATE_SOURCE_SESSION_SLOT,
	UPDATE_SOURCE_SESSION_SLOT,
	type FireSkillEventAttributes,
	type SkillCreateFlowTouchPoint,
	type SkillFlowSource,
	type SkillDeleteFlowTouchPoint,
	type SkillFailureReason,
	type SkillFlowSessionHandle,
	type SkillUpdateFlowTouchPoint,
	type SkillViewFlowTouchPoint,
} from './types';

/** Extra attributes for the created and failed events. The session and the failure reason set the rest. */
export type SkillEventOptions = Omit<
	FireSkillEventAttributes,
	'touchPoint' | 'traceId' | 'failureReason'
>;

export type SkillCreatedOptions = SkillEventOptions & {
	/** Leaves the session open, for a flow that creates several skills in one pass. */
	keepSession?: boolean;
};

const SKILL_CREATE_ACTION_SUBJECT = 'solutionCreateFlowSkill';
const SKILL_UPDATE_ACTION_SUBJECT = 'solutionUpdateFlowSkill';
const SKILL_DELETE_ACTION_SUBJECT = 'solutionDeleteFlowSkill';
const SKILL_DRAFT_CARD_ACTION_SUBJECT = 'solutionCreateFlowDraftSkill';
const ANALYTICS_OBJECT_TYPE = 'skill';
// Every skill create flow event goes through one channel. The atlaskit channel's listener only accepts UI events, so it cannot carry the track event.
const AI_MATE_CHANNEL = 'aiMate';
// The atlaskit listener used to add this tag, so it has to be set explicitly now.
const ATLASKIT_TAGS = ['atlaskit'];

const isSessionDraftOrStarted = (session: SkillFlowSessionHandle | undefined): boolean =>
	session?.state?.stage === 'draftCardViewed' || session?.state?.stage === 'startedSkillFlow';

export const useCustomSkillsAnalytics = (): {
	fireSkillCreateFlowViewed: (
		touchPoint: SkillCreateFlowTouchPoint | SkillViewFlowTouchPoint,
	) => void;
	fireSkillCreateFlowStart: (
		touchPoint: SkillCreateFlowTouchPoint,
		options?: { isUpdate?: boolean },
	) => void;
	fireSkillUpdateFlowStarted: (touchPoint: SkillUpdateFlowTouchPoint) => void;
	fireSkillDeleteFlowStarted: (touchPoint: SkillDeleteFlowTouchPoint) => void;
	fireSkillDeleted: () => void;
	fireSkillDeleteFailed: (failureReason: SkillFailureReason) => void;
	fireSkillDraftCardViewed: (options?: { isUpdate?: boolean }) => void;
	getSkillDraftCardSession: () => SkillFlowSessionHandle | undefined;
	fireSkillCreated: (source: SkillFlowSource, options?: SkillCreatedOptions) => void;
	fireSkillCreateFailed: (
		source: SkillFlowSource,
		failureReason?: SkillFailureReason,
		options?: SkillEventOptions,
	) => void;
	fireSkillUpdated: (source: SkillFlowSource) => void;
	fireSkillUpdateFailed: (source: SkillFlowSource, failureReason?: SkillFailureReason) => void;
} => {
	di(useAnalyticsEvents);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireSkillEvent = useCallback(
		(
			actionSubject: string,
			action: 'viewed' | 'started' | 'created' | 'updated' | 'deleted' | 'failed',
			attributes: FireSkillEventAttributes,
			eventType?: 'track',
		) => {
			createAnalyticsEvent({
				...(eventType && { eventType }),
				tags: ATLASKIT_TAGS,
				action,
				actionSubject,
				attributes: {
					objectType: ANALYTICS_OBJECT_TYPE,
					...attributes,
				},
			}).fire(AI_MATE_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	// solutionCreateFlow viewed event only happens for skill creation (modal and page).
	const fireSkillCreateFlowViewed = useCallback(
		(touchPoint: SkillCreateFlowTouchPoint | SkillViewFlowTouchPoint) => {
			fireSkillEvent(SKILL_CREATE_ACTION_SUBJECT, 'viewed', {
				touchPoint,
			});
		},
		[fireSkillEvent],
	);

	// solutionCreateFlow started. Every start begins a new session with a new traceId; the viewed event has none.
	const fireSkillCreateFlowStart = useCallback(
		(touchPoint: SkillCreateFlowTouchPoint, { isUpdate }: { isUpdate?: boolean } = {}) => {
			const session = skillCreateFlowSession.start(touchPoint, 'startedSkillFlow');

			fireSkillEvent(SKILL_CREATE_ACTION_SUBJECT, 'started', {
				touchPoint,
				traceId: session.state?.traceId,
				...(isUpdate && { isUpdate }),
			});
		},
		[fireSkillEvent],
	);

	// solutionUpdateFlow started. Every start begins a new session with a new traceId.
	const fireSkillUpdateFlowStarted = useCallback(
		(touchPoint: SkillUpdateFlowTouchPoint) => {
			const session = skillCreateFlowSession.start(touchPoint, 'startedSkillFlow');

			fireSkillEvent(SKILL_UPDATE_ACTION_SUBJECT, 'started', {
				touchPoint,
				traceId: session.state?.traceId,
			});
		},
		[fireSkillEvent],
	);

	// The chat session that could lead to a draft card, if one is in progress. Being started is what
	// produces a draft card, and a session already followed to a draft card does not count: the next
	// card starts a session of its own.
	const getSkillDraftCardSession = useCallback(() => {
		const session = skillCreateFlowSession.getFromSessionSlot('chatSkill');

		if (session?.state?.stage === 'startedSkillFlow') {
			return session;
		}

		return undefined;
	}, []);

	// Takes no touch point: the draft card does not know what led the user there, so the touch point
	// and trace id come from the chat session, and neither is reported without one.
	const fireSkillDraftCardViewed = useCallback(
		({ isUpdate }: { isUpdate?: boolean } = {}) => {
			// This can only happen from chat skill flow
			const session = getSkillDraftCardSession();

			const attrs: Partial<FireSkillEventAttributes> = { ...(isUpdate && { isUpdate }) };

			if (session?.state) {
				session.advanceTo('draftCardViewed');
				attrs.traceId = session.state.traceId;
				attrs.touchPoint = session.state.touchPoint;
			}

			fireSkillEvent(SKILL_DRAFT_CARD_ACTION_SUBJECT, 'viewed', attrs);
		},
		[fireSkillEvent, getSkillDraftCardSession],
	);

	// Reports the session of the create flow the source belongs to, while it is started (or, for chat,
	// at its draft card). Returns it so the caller can end it.
	const fireSkillCreateOutcome = useCallback(
		(
			action: 'created' | 'failed',
			source: SkillFlowSource,
			failureReason?: SkillFailureReason,
			options: SkillEventOptions = {},
		) => {
			const sessionSlot = CREATE_SOURCE_SESSION_SLOT[source];

			const session = skillCreateFlowSession.getFromSessionSlot(sessionSlot);

			const reportedSession = isSessionDraftOrStarted(session) ? session : undefined;

			fireSkillEvent(
				SKILL_CREATE_ACTION_SUBJECT,
				action,
				{
					...(reportedSession?.state && {
						traceId: reportedSession.state.traceId,
						touchPoint: reportedSession.state.touchPoint,
					}),
					...(failureReason && { failureReason }),
					...options,
				},
				'track',
			);

			return reportedSession;
		},
		[fireSkillEvent],
	);

	/**
	 * A skill can be created from 3 different places:
	 * - chat: when the skill is created from Rovo chat
	 * - modal: when the skill is created from the skill modal
	 * - page: when the skill is created from the skill page form
	 *
	 * You need to select from where the skill was created to trigger this event
	 *
	 * The session ends here, unless `keepSession` is set. A flow that creates several skills in one
	 * pass, such as cloning shared skills, keeps it until the next start replaces it.
	 */
	const fireSkillCreated = useCallback(
		(source: SkillFlowSource, { keepSession, ...eventOptions }: SkillCreatedOptions = {}) => {
			const session = fireSkillCreateOutcome('created', source, undefined, eventOptions);

			if (!keepSession) {
				session?.clear();
			}
		},
		[fireSkillCreateOutcome],
	);

	// Leaves the session as it is, so a retry from the same form continues the same pass.
	const fireSkillCreateFailed = useCallback(
		(source: SkillFlowSource, failureReason?: SkillFailureReason, options?: SkillEventOptions) => {
			fireSkillCreateOutcome('failed', source, failureReason, options);
		},
		[fireSkillCreateOutcome],
	);

	// Reports the session of the update flow the source belongs to, as for a created skill.
	const fireSkillUpdateOutcome = useCallback(
		(action: 'updated' | 'failed', source: SkillFlowSource, failureReason?: SkillFailureReason) => {
			const sessionSlot = UPDATE_SOURCE_SESSION_SLOT[source];

			const session = skillCreateFlowSession.getFromSessionSlot(sessionSlot);

			const reportedSession = isSessionDraftOrStarted(session) ? session : undefined;

			fireSkillEvent(
				SKILL_UPDATE_ACTION_SUBJECT,
				action,
				{
					...(reportedSession?.state && {
						traceId: reportedSession.state.traceId,
						touchPoint: reportedSession.state.touchPoint,
					}),
					...(failureReason && { failureReason }),
				},
				'track',
			);

			return reportedSession;
		},
		[fireSkillEvent],
	);

	// The skill is updated, so the session ends here, as it does for a created skill.
	const fireSkillUpdated = useCallback(
		(source: SkillFlowSource) => {
			const session = fireSkillUpdateOutcome('updated', source);
			session?.clear();
		},
		[fireSkillUpdateOutcome],
	);

	// Leaves the session as it is, so a retry continues the same pass.
	const fireSkillUpdateFailed = useCallback(
		(source: SkillFlowSource, failureReason?: SkillFailureReason) => {
			fireSkillUpdateOutcome('failed', source, failureReason);
		},
		[fireSkillUpdateOutcome],
	);

	// Each start is a new pass through the delete flow, so a new session and a new trace id every time.
	const fireSkillDeleteFlowStarted = useCallback(
		(touchPoint: SkillDeleteFlowTouchPoint) => {
			const session = skillCreateFlowSession.start(touchPoint, 'startedSkillFlow');

			fireSkillEvent(SKILL_DELETE_ACTION_SUBJECT, 'started', {
				touchPoint,
				traceId: session.state?.traceId,
			});
		},
		[fireSkillEvent],
	);

	// Reports the delete session while it is started. Returns it so the caller can end it.
	const fireSkillDeleteOutcome = useCallback(
		(action: 'deleted' | 'failed', failureReason?: SkillFailureReason) => {
			const session = skillCreateFlowSession.getFromSessionSlot('deleteDialog');
			const reportedSession = session?.state?.stage === 'startedSkillFlow' ? session : undefined;

			fireSkillEvent(
				SKILL_DELETE_ACTION_SUBJECT,
				action,
				{
					...(reportedSession?.state && {
						traceId: reportedSession.state.traceId,
						touchPoint: reportedSession.state.touchPoint,
					}),
					...(failureReason && { failureReason }),
				},
				'track',
			);

			return reportedSession;
		},
		[fireSkillEvent],
	);

	// The skill is gone, so the session ends here, as it does for a created skill.
	const fireSkillDeleted = useCallback(() => {
		const session = fireSkillDeleteOutcome('deleted');
		session?.clear();
	}, [fireSkillDeleteOutcome]);

	// Leaves the session as it is, so a retry from the same modal continues the same pass.
	const fireSkillDeleteFailed = useCallback(
		(failureReason: SkillFailureReason) => {
			fireSkillDeleteOutcome('failed', failureReason);
		},
		[fireSkillDeleteOutcome],
	);

	return {
		fireSkillDeleteFlowStarted,
		fireSkillDeleted,
		fireSkillDeleteFailed,
		fireSkillUpdated,
		fireSkillUpdateFailed,
		fireSkillCreated,
		fireSkillCreateFailed,
		fireSkillCreateFlowViewed,
		fireSkillCreateFlowStart,
		fireSkillUpdateFlowStarted,
		fireSkillDraftCardViewed,
		getSkillDraftCardSession,
	};
};
